<script lang="ts">
	/**
	 * ScrollReveal - Animates children into view when scrolled into viewport
	 *
	 * @example
	 * <ScrollReveal delay={200}>
	 *   <div>This content fades in when scrolled into view</div>
	 * </ScrollReveal>
	 */
	import { onMount } from 'svelte';
	import { prefersReducedMotion, isMobileDevice } from '$lib/utils/motion';

	interface Props {
		/** Delay before animation starts (ms) */
		delay?: number;
		/** Animation duration (ms) */
		duration?: number;
		/** Initial Y offset in pixels */
		y?: number;
		/** Only animate once (don't re-animate on scroll out/in) */
		once?: boolean;
		/** Additional CSS classes */
		class?: string;
		children: import('svelte').Snippet;
	}

	let {
		delay = 0,
		duration = 1000,
		y = 40,
		once = true,
		class: className = '',
		children
	}: Props = $props();

	let element: HTMLElement;
	let isVisible = $state(false);
	let mobile = $state(false);

	onMount(() => {
		mobile = isMobileDevice();

		// Immediately show content if user prefers reduced motion
		if (prefersReducedMotion()) {
			isVisible = true;
			return;
		}

		// On mobile, skip stagger delays (cards are single-column, not grid)
		const effectiveDelay = mobile ? 0 : delay;

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						if (effectiveDelay > 0) {
							setTimeout(() => {
								isVisible = true;
							}, effectiveDelay);
						} else {
							isVisible = true;
						}
						if (once) observer.disconnect();
					} else if (!once) {
						isVisible = false;
					}
				});
			},
			{ threshold: 0.1, rootMargin: '0px 0px -80px 0px' }
		);

		observer.observe(element);

		return () => observer.disconnect();
	});
</script>

<div
	bind:this={element}
	class="{mobile ? '' : 'will-change-transform'} {className}"
	style="
		opacity: {isVisible ? 1 : 0};
		transform: translateY({isVisible ? 0 : y}px) scale({isVisible ? 1 : 0.97});
		{mobile ? '' : `filter: blur(${isVisible ? 0 : 8}px);`}
		transition: opacity {mobile ? 400 : duration}ms cubic-bezier(0.25, 1, 0.5, 1),
					transform {mobile ? 400 : duration}ms cubic-bezier(0.25, 1, 0.5, 1){mobile
		? ''
		: `,
					filter ${duration * 0.8}ms cubic-bezier(0.25, 1, 0.5, 1)`};
	"
>
	{@render children()}
</div>
